import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Input, Button, Typography } from "@material-tailwind/react";
import ShowLoader from "@/components/Loader";
import { ArrowLeftIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/solid";
import ScrollToTop from "@/components/ScrollToTop";
import { getSettingData, update, resetStatus } from "@/store/slices/settingSlice";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

const getValidationSchema = (t) => yup.object().shape({
    flat_margin: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative")),
    percentage_margin: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative"))
        .max(1000, t("validation.cannotExceed1000")),
    flat_discount: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative")),
    percentage_discount: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative"))
        .max(1000, t("validation.cannotExceed1000")),
    flat_commission: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative")),
    percentage_commission: yup
        .number()
        .required(t("validation.required"))
        .typeError(t("validation.mustBeNumber"))
        .min(0, t("validation.cannotBeNegative"))
        .max(1000, t("validation.cannotExceed1000")),
});

export function Settings() {
    const [copied, setCopied] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation("app-setting");
    const validationSchema = useMemo(() => getValidationSchema(t), [t]);

    const vendorRegistrationLink = `${window.location.origin}/vendor/register`;

    const handleCopyLink = () => {
        navigator.clipboard.writeText(vendorRegistrationLink);
        setCopied(true);
        Toaster.success(t("toaster.linkCopied"));
        setTimeout(() => setCopied(false), 2000);
    };

    const { settings, loading } = useSelector((state) => state.setting);

    useEffect(() => {
        dispatch(getSettingData());
    }, [dispatch]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            flat_margin: settings?.flat_margin || 0,
            percentage_margin: settings?.percentage_margin || 0,
            flat_discount: settings?.flat_discount || 0,
            percentage_discount: settings?.percentage_discount || 0,
            flat_commission: settings?.flat_commission || 0,
            percentage_commission: settings?.percentage_commission || 0
        }
    });

    useEffect(() => {
        if (settings) {
            reset({
                flat_margin: settings.flat_margin,
                percentage_margin: settings.percentage_margin,
                flat_discount: settings.flat_discount,
                percentage_discount: settings.percentage_discount,
                flat_commission: settings.flat_commission,
                percentage_commission: settings.percentage_commission
            });
        }
    }, [settings, reset]);

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(update(data)).unwrap();
            if (result?.status) {
                Toaster.success(t("toaster.updateSuccess"));
                dispatch(resetStatus());
            } else {
                Toaster.error(result.message || t("toaster.somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t("toaster.updateFailed"));
            dispatch(resetStatus());
        }
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading && <ShowLoader />}
            <ScrollToTop />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">

                <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
                        <div className="flex items-center gap-2 cursor-pointer">
                            <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                                {t("title")}
                            </Typography>
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white mt-6"
                        >
                            {loading ? t("saving") : t("update")}
                        </Button>
                    </div>
                    {/* <div className="flex items-center gap-1">
                        <Link to={-1} className="flex items-center text-blue-600 hover:text-blue-800">
                            <ArrowLeftIcon className="h-4 w-4 mr-1" />
                            {t("back")}
                        </Link>
                    </div> */}
                </div>
                <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                    <Typography variant="h6" color="blue-gray" className="mb-5">
                        {t("vendorRegistrationLink")}
                    </Typography>
                    <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full">
                        <div className="flex-1">
                            <Input
                                readOnly
                                value={vendorRegistrationLink}
                                label={t("vendorRegistrationUrl")}
                                className="w-full"
                            />
                        </div>
                        <div className="w-full sm:w-[200px] flex-shrink-0">
                            <Button
                                type="button"
                                disabled={loading}
                                onClick={handleCopyLink}
                                className={`w-full flex items-center justify-center gap-2 px-5 py-2.5 capitalize font-semibold text-white rounded-lg shadow-sm transition-all duration-300
                            ${copied ? "bg-green-600 hover:bg-green-700" : "bg-black hover:bg-gray-800"}`}
                            >
                                <ClipboardDocumentCheckIcon className="h-5 w-5" />
                                {copied ? t("copied") : t("copyLink")}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                    <Typography variant="h6" color="blue-gray" className="mb-5">
                        {t("rfqMarginDiscountLimits")}
                    </Typography>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxFlatMargin")}
                                className="w-full"
                                {...register('flat_margin')}
                                min={0}
                                step="any"
                            />
                            {errors.flat_margin && (
                                <p className="text-red-500 text-sm mt-1">{errors.flat_margin.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxFlatMarginHelp")}
                            </p>
                        </div>

                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxPercentageMargin")}
                                className="w-full"
                                {...register('percentage_margin')}
                                min={0}
                                step="any"
                            />
                            {errors.percentage_margin && (
                                <p className="text-red-500 text-sm mt-1">{errors.percentage_margin.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxPercentageMarginHelp")}
                            </p>
                        </div>

                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxFlatDiscount")}
                                className="w-full"
                                {...register('flat_discount')}
                                min={0}
                                step="any"
                            />
                            {errors.flat_discount && (
                                <p className="text-red-500 text-sm mt-1">{errors.flat_discount.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxFlatDiscountHelp")}
                            </p>
                        </div>

                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxPercentageDiscount")}
                                className="w-full"
                                {...register('percentage_discount')}
                                min={0}
                                step="any"
                            />
                            {errors.percentage_discount && (
                                <p className="text-red-500 text-sm mt-1">{errors.percentage_discount.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxPercentageDiscountHelp")}
                            </p>
                        </div>

                    </div>
                </div>

                <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                    <Typography variant="h6" color="blue-gray" className="mb-5">
                        {t("setDefaultCommission")}
                    </Typography>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5">
                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxFlatCommission")}
                                className="w-full"
                                {...register('flat_commission')}
                                min={0}
                                step="any"
                            />
                            {errors.flat_commission && (
                                <p className="text-red-500 text-sm mt-1">{errors.flat_commission.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxFlatCommissionHelp")}
                            </p>
                        </div>
                        <div className="flex flex-col">
                            <Input
                                type="number"
                                label={t("maxPercentageCommission")}
                                className="w-full"
                                {...register('percentage_commission')}
                                min={0}
                                step="any"
                            />
                            {errors.percentage_commission && (
                                <p className="text-red-500 text-sm mt-1">{errors.percentage_commission.message}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                {t("maxPercentageCommissionHelp")}
                            </p>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default Settings;
